<script lang="ts">
	import type { Snippet } from 'svelte';
	import { onMount } from 'svelte';
	import { getModalContext } from '@astlai/common/contexts/modal.context.svelte.ts';
	import {
		setSelectInteractiveContext,
		setSelectRegistryContext,
		type OptionMeta,
		type SelectValue
	} from './select-context';
	import {
		getFirstEnabledIndex,
		getLastEnabledIndex,
		getNextIndex,
		matchTypeahead,
		optionDomId,
		removeOption,
		upsertOption
	} from './select-logic';

	interface Props {
		children: Snippet;
		footer?: Snippet;
		value: SelectValue | SelectValue[] | null | undefined;
		multiple: boolean;
		idPrefix: string;
		onSelect: (value: SelectValue) => void;
	}
	let { children, footer, value, multiple, idPrefix, onSelect }: Props = $props();

	const modalCtx = getModalContext();

	let options: OptionMeta[] = $state([]);
	let highlightedIndex = $state(-1);
	let containerEl: HTMLDivElement | undefined = $state();
	let typeaheadQuery = $state('');
	let typeaheadTimeout: ReturnType<typeof setTimeout> | undefined;

	const selectedValues = $derived(
		Array.isArray(value) ? value : value !== undefined && value !== null ? [value] : []
	);
	const highlightedValue = $derived(options[highlightedIndex]?.value);

	setSelectRegistryContext({
		register(meta) {
			options = upsertOption(options, meta);
		},
		unregister(unregisterValue) {
			options = removeOption(options, unregisterValue);
		}
	});

	setSelectInteractiveContext({
		multiple,
		idPrefix,
		isSelected: (v) => selectedValues.includes(v),
		isHighlighted: (v) => v === highlightedValue,
		select: (v) => {
			onSelect(v);
			if (!multiple) {
				modalCtx.closeModal();
			}
		},
		setHighlighted: (v) => {
			const index = options.findIndex((o) => o.value === v);
			if (index !== -1) {
				highlightedIndex = index;
			}
		}
	});

	$effect(() => {
		if (highlightedIndex === -1 && options.length > 0) {
			const selectedIndex = options.findIndex((o) => selectedValues.includes(o.value));
			highlightedIndex = selectedIndex !== -1 ? selectedIndex : getFirstEnabledIndex(options);
		}
	});

	onMount(() => {
		containerEl?.focus();
		const selectedEl = containerEl?.querySelector<HTMLElement>('.selected');
		if (selectedEl && containerEl) {
			const containerRect = containerEl.getBoundingClientRect();
			const selectedRect = selectedEl.getBoundingClientRect();
			const offset = selectedRect.top - containerRect.top + containerEl.scrollTop;
			containerEl.scrollTop = offset - containerEl.clientHeight / 2 + selectedRect.height / 2;
		}
	});

	function selectHighlighted() {
		const option = options[highlightedIndex];
		if (option && !option.disabled) {
			onSelect(option.value);
			if (!multiple) {
				modalCtx.closeModal();
			}
		}
	}

	function handleTypeahead(char: string) {
		typeaheadQuery += char.toLowerCase();
		clearTimeout(typeaheadTimeout);
		typeaheadTimeout = setTimeout(() => {
			typeaheadQuery = '';
		}, 500);
		const index = matchTypeahead(options, typeaheadQuery, highlightedIndex);
		if (index !== -1) {
			highlightedIndex = index;
		}
	}

	function handleKeydown(e: KeyboardEvent) {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			highlightedIndex = getNextIndex(options, highlightedIndex, 1);
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			highlightedIndex = getNextIndex(options, highlightedIndex, -1);
		} else if (e.key === 'Home') {
			e.preventDefault();
			highlightedIndex = getFirstEnabledIndex(options);
		} else if (e.key === 'End') {
			e.preventDefault();
			highlightedIndex = getLastEnabledIndex(options);
		} else if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			selectHighlighted();
		} else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
			handleTypeahead(e.key);
		}
	}
</script>

<div
	bind:this={containerEl}
	class="select-dropdown"
	role="listbox"
	aria-multiselectable={multiple || undefined}
	aria-activedescendant={highlightedIndex !== -1 && highlightedValue !== undefined
		? optionDomId(idPrefix, highlightedValue)
		: undefined}
	tabindex="0"
	onkeydown={handleKeydown}
>
	{@render children()}
	{#if footer}
		<div class="select-dropdown-footer">
			{@render footer()}
		</div>
	{/if}
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.select-dropdown {
		@include lib.flex-column();
		gap: 0.25rem;
		padding: 0.25rem;
		width: 100%;
		max-height: 320px;
		overflow-y: auto;

		&:focus-visible {
			outline: none;
		}
	}

	.select-dropdown-footer {
		@include lib.flex-column();
		border-top: 1px solid var(--border-primary);
	}
</style>
